import { supabase } from '../lib/supabase';

/**
 * Service for managing artist API keys and usage tracking
 * Handles all database operations for API key management
 */
export const apiKeyService = {
  /**
   * Generate a new API key for the authenticated artist
   * @param {Object} keyData - API key configuration
   * @returns {Promise<Object>} Created API key with credentials
   */
  async generateApiKey(keyData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Call database functions to generate key and secret
      const { data: keyResult, error: keyError } = await supabase?.rpc('generate_api_key');
      if (keyError) throw keyError;

      const { data: secretResult, error: secretError } = await supabase?.rpc('generate_api_secret');
      if (secretError) throw secretError;

      // Insert new API key
      const { data, error } = await supabase?.from('artist_api_keys')?.insert({
          artist_id: user?.id,
          key_name: keyData?.keyName,
          api_key: keyResult,
          api_secret: secretResult,
          permissions: keyData?.permissions || ['read'],
          usage_limit: keyData?.usageLimit || 10000,
          rate_limit_per_minute: keyData?.rateLimitPerMinute || 60,
          allowed_ip_addresses: keyData?.allowedIpAddresses || null,
          expires_at: keyData?.expiresAt || null
        })?.select()?.single();

      if (error) throw error;

      // Convert to camelCase for React
      return {
        id: data?.id,
        artistId: data?.artist_id,
        keyName: data?.key_name,
        apiKey: data?.api_key,
        apiSecret: data?.api_secret,
        permissions: data?.permissions,
        status: data?.status,
        usageLimit: data?.usage_limit,
        currentUsage: data?.current_usage,
        rateLimitPerMinute: data?.rate_limit_per_minute,
        allowedIpAddresses: data?.allowed_ip_addresses,
        expiresAt: data?.expires_at,
        lastUsedAt: data?.last_used_at,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error generating API key:', error);
      throw error;
    }
  },

  /**
   * Get all API keys for the authenticated artist
   * @returns {Promise<Array>} List of API keys
   */
  async getApiKeys() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('artist_api_keys')?.select('*')?.eq('artist_id', user?.id)?.order('created_at', { ascending: false });

      if (error) throw error;

      // Convert to camelCase
      return data?.map(key => ({
        id: key?.id,
        artistId: key?.artist_id,
        keyName: key?.key_name,
        apiKey: key?.api_key,
        apiSecret: key?.api_secret,
        permissions: key?.permissions,
        status: key?.status,
        usageLimit: key?.usage_limit,
        currentUsage: key?.current_usage,
        rateLimitPerMinute: key?.rate_limit_per_minute,
        allowedIpAddresses: key?.allowed_ip_addresses,
        expiresAt: key?.expires_at,
        lastUsedAt: key?.last_used_at,
        createdAt: key?.created_at,
        updatedAt: key?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching API keys:', error);
      throw error;
    }
  },

  /**
   * Revoke an API key (set status to revoked)
   * @param {string} keyId - API key ID to revoke
   * @returns {Promise<void>}
   */
  async revokeApiKey(keyId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase?.from('artist_api_keys')?.update({ status: 'revoked' })?.eq('id', keyId)?.eq('artist_id', user?.id);

      if (error) throw error;
    } catch (error) {
      console.error('Error revoking API key:', error);
      throw error;
    }
  },

  /**
   * Regenerate an API key (create new key/secret, revoke old one)
   * @param {string} keyId - API key ID to regenerate
   * @returns {Promise<Object>} New API key with credentials
   */
  async regenerateApiKey(keyId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get existing key details
      const { data: existingKey, error: fetchError } = await supabase?.from('artist_api_keys')?.select('*')?.eq('id', keyId)?.eq('artist_id', user?.id)?.single();

      if (fetchError) throw fetchError;

      // Revoke old key
      await this.revokeApiKey(keyId);

      // Generate new key with same settings
      return await this.generateApiKey({
        keyName: existingKey?.key_name,
        permissions: existingKey?.permissions,
        usageLimit: existingKey?.usage_limit,
        rateLimitPerMinute: existingKey?.rate_limit_per_minute,
        allowedIpAddresses: existingKey?.allowed_ip_addresses,
        expiresAt: existingKey?.expires_at
      });
    } catch (error) {
      console.error('Error regenerating API key:', error);
      throw error;
    }
  },

  /**
   * Get usage statistics for an API key
   * @param {string} keyId - API key ID
   * @returns {Promise<Object>} Usage statistics
   */
  async getUsageStatistics(keyId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.rpc('get_api_key_usage_stats', {
        key_uuid: keyId
      });

      if (error) throw error;

      // Convert to camelCase
      return {
        totalRequests: data?.[0]?.total_requests || 0,
        successfulRequests: data?.[0]?.successful_requests || 0,
        failedRequests: data?.[0]?.failed_requests || 0,
        avgResponseTime: data?.[0]?.avg_response_time || 0,
        requestsToday: data?.[0]?.requests_today || 0,
        requestsThisWeek: data?.[0]?.requests_this_week || 0,
        requestsThisMonth: data?.[0]?.requests_this_month || 0
      };
    } catch (error) {
      console.error('Error fetching usage statistics:', error);
      throw error;
    }
  },

  /**
   * Get usage logs for an API key with pagination
   * @param {string} keyId - API key ID
   * @param {number} limit - Number of logs to fetch
   * @param {number} offset - Offset for pagination
   * @returns {Promise<Array>} Usage logs
   */
  async getUsageLogs(keyId, limit = 100, offset = 0) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('api_key_usage_logs')?.select('*')?.eq('api_key_id', keyId)?.order('request_timestamp', { ascending: false })?.range(offset, offset + limit - 1);

      if (error) throw error;

      // Convert to camelCase
      return data?.map(log => ({
        id: log?.id,
        apiKeyId: log?.api_key_id,
        endpoint: log?.endpoint,
        method: log?.method,
        statusCode: log?.status_code,
        responseTimeMs: log?.response_time_ms,
        ipAddress: log?.ip_address,
        userAgent: log?.user_agent,
        errorMessage: log?.error_message,
        requestTimestamp: log?.request_timestamp
      }));
    } catch (error) {
      console.error('Error fetching usage logs:', error);
      throw error;
    }
  },

  /**
   * Update API key settings
   * @param {string} keyId - API key ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated API key
   */
  async updateApiKey(keyId, updates) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Convert camelCase to snake_case for database
      const dbUpdates = {};
      if (updates?.keyName !== undefined) dbUpdates.key_name = updates?.keyName;
      if (updates?.permissions !== undefined) dbUpdates.permissions = updates?.permissions;
      if (updates?.usageLimit !== undefined) dbUpdates.usage_limit = updates?.usageLimit;
      if (updates?.rateLimitPerMinute !== undefined) dbUpdates.rate_limit_per_minute = updates?.rateLimitPerMinute;
      if (updates?.allowedIpAddresses !== undefined) dbUpdates.allowed_ip_addresses = updates?.allowedIpAddresses;
      if (updates?.expiresAt !== undefined) dbUpdates.expires_at = updates?.expiresAt;

      const { data, error } = await supabase?.from('artist_api_keys')?.update(dbUpdates)?.eq('id', keyId)?.eq('artist_id', user?.id)?.select()?.single();

      if (error) throw error;

      // Convert to camelCase
      return {
        id: data?.id,
        artistId: data?.artist_id,
        keyName: data?.key_name,
        apiKey: data?.api_key,
        apiSecret: data?.api_secret,
        permissions: data?.permissions,
        status: data?.status,
        usageLimit: data?.usage_limit,
        currentUsage: data?.current_usage,
        rateLimitPerMinute: data?.rate_limit_per_minute,
        allowedIpAddresses: data?.allowed_ip_addresses,
        expiresAt: data?.expires_at,
        lastUsedAt: data?.last_used_at,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating API key:', error);
      throw error;
    }
  }
};